import { useImmutableChannel } from '@/api/hooks'
import { fetchChannelChildren } from '@/app/util/channel-api'
import { IChannel } from '@giphy/js-types'
import { useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import useFetchData from 'ui/src/hooks/use-fetch-data'
import { create } from 'zustand'

type Store = {
    [id: string]: IChannel[]
}

const initialState: Store = {}

const useDataStore = create<Store>()((_) => initialState)

export const updateChannelChildren = (channelId: string | number, channels: IChannel[]): void => {
    useDataStore.setState((state) => ({ ...state, [channelId]: channels }))
}

export const getChannelChildren = (channelId: string | number): IChannel[] => {
    return useDataStore.getState()[channelId] || []
}

export const useChannelChildren = (channelId: string | number, defaultC: IChannel[] = []): IChannel[] => {
    return useDataStore((state) => state[channelId]) || defaultC
}

export const useChannelChildrenInfinite = (id: number | string) => {
    const { data: channel, isLoading } = useImmutableChannel(id)
    const children = useChannelChildren(id, channel?.children)
    const fetchData = useCallback(
        async (offset: number) => {
            return await fetchChannelChildren(id, '', {
                offset,
                limit: 20,
            })
        },
        [id]
    )
    const { ref, inView } = useInView({ rootMargin: '0px 0px 250px 0px' })
    // use this to fetch but rely on Zustand data for children rendering
    const { doneFetching, gifs: kids } = useFetchData<IChannel>({
        initialData: children,
        fetchData,
        triggerFetch: inView,
        checkDone: () => children.length === channel?.num_children,
    })
    useEffect(() => {
        updateChannelChildren(id, kids)
    }, [id, kids])
    return { doneFetching, isLoading, ref, channel, children }
}
